import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import {
  getDynamicFormFieldFilteringOptions,
  getclearFormFieldModel,
} from "../../common/EntitiesManagement";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import ModalBackground from "../../components/common/ModalBackground";
import TableComponent from "../../components/common/TableComponent";
import TrueFalseImage from "../../components/TrueFalseImage";
import {
  generateNoDataValues,
  getFieldTypeName,
  getFiltersInfo,
} from "../../common/utils";
import AddOrEditFormField from "./components/AddOrEditFormField";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function FormFieldList() {
  const {
    formFieldModel,
    setFormFieldModel,
    formFieldData,
    FORM_FIELDS_ENDPOINT,
    config,
    pageIndexFormFields,
    setPageIndexFormFields,
    toggleVisibleModalConfirm,
    deleteEntity,
    createEntity,
    updateEntity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();
  const { id } = useParams();
  const [formFieldDataFiltered, setFormFieldDataFiltered] = useState(
    formFieldData.Data
  );

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [trueFalseTexts, setTrueFalseTexts] = useState(undefined);
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getDynamicFormFieldFilteringOptions());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const formFieldDataObj = formFieldData.Data.find(
        (formFieldObj) => formFieldObj.Id === Number(id)
      );
      setFormFieldModel(formFieldDataObj);
    }
  }, [id]);

  useEffect(() => {
    setFormFieldDataFiltered(formFieldData.Data);
  }, [formFieldData]);

  const validateOperationResult = (result, isDelete) => {
    setFormFieldModel(getclearFormFieldModel());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([FORM_FIELDS_ENDPOINT, config]);
    if (isDelete) {
      if (pageIndexFormFields !== 0 && formFieldData.Data.length === 1) {
        setPageIndexFormFields(pageIndexFormFields - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${FORM_FIELDS_ENDPOINT}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${FORM_FIELDS_ENDPOINT}/${data.Id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${FORM_FIELDS_ENDPOINT}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const disableSaveChanges = () =>
    !formFieldModel?.ValueType ||
    formFieldModel?.ValueType.replaceAll(" ", "").length <= 0 ||
    !formFieldModel?.Title ||
    formFieldModel?.Title.replaceAll(" ", "").length <= 0;

  const getColumnTitles = () => {
    return [
      localize.field_type,
      localize.title,
      localize.help_text,
      localize.visible_on_mobile,
      localize.mandatory,
      // localize.available_on_review,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.form_fields}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={FORM_FIELDS_ENDPOINT}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="dynamic_form_field"
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_form_field}
          onClickToSubmit={() => {
            handleUpdateRequest(
              formFieldModel?.ValueType === "BOOLEAN"
                ? {
                    ...formFieldModel,
                    Options: JSON.stringify(trueFalseTexts),
                  }
                : formFieldModel
            );
            setShowEditModal(false);
            setTrueFalseTexts(undefined);
            setFormFieldModel(getclearFormFieldModel());
          }}
          onClickToCancel={() => {
            setTrueFalseTexts(undefined);
            setShowEditModal(false);
            setFormFieldModel(getclearFormFieldModel());
          }}
          Component={
            <AddOrEditFormField
              model={formFieldModel}
              setModel={setFormFieldModel}
              trueFalseTexts={trueFalseTexts}
              setTrueFalseTexts={setTrueFalseTexts}
            />
          }
          validators={disableSaveChanges()}
        />
      )}

      {showDeleteModal && (
        <DeleteConfirmationMessage
          entityName={formFieldModel.Title}
          entityType={localize.form_field}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setShowDeleteModal(false);
            setTrueFalseTexts(undefined);
            setFormFieldModel(getclearFormFieldModel());
          }}
          onClickForConfirm={() => {
            handleDeleteRequest(formFieldModel.Id);
            setShowDeleteModal(false);
            setTrueFalseTexts(undefined);
            setFormFieldModel(getclearFormFieldModel());
          }}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.form_fields}
            subtitle={localize.form_fields_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => {
              setShowPrinterModal(true);
            }}
            entityName="form_field_filter"
            placeHolderText={localize.search_by_title}
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_form_field}
                onClickToSubmit={() => {
                  handleCreateRequest(
                    formFieldModel?.ValueType === "BOOLEAN"
                      ? {
                          ...formFieldModel,
                          Options: JSON.stringify(trueFalseTexts),
                        }
                      : formFieldModel
                  );
                  setShowAddModal(false);
                  setTrueFalseTexts(undefined);
                  setFormFieldModel(getclearFormFieldModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setTrueFalseTexts(undefined);
                  setFormFieldModel(getclearFormFieldModel());
                }}
                Component={
                  <AddOrEditFormField
                    model={formFieldModel}
                    setModel={setFormFieldModel}
                    trueFalseTexts={trueFalseTexts}
                    setTrueFalseTexts={setTrueFalseTexts}
                  />
                }
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexFormFields}
            setPageIndex={setPageIndexFormFields}
            dataArray={formFieldData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          formFieldData.Data?.length !== 0
            ? formFieldDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        if (item.Options) {
                          if (item.Options && item.ValueType === "BOOLEAN") {
                            const options = JSON.parse(item.Options);
                            setTrueFalseTexts({
                              ...options,
                            });
                          }
                        }
                        setFormFieldModel({
                          ...item,
                          ValueTypeData: {
                            Id: item.ValueType,
                            Name: getFieldTypeName(item.ValueType),
                          },
                        });
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setFormFieldModel(item);
                        setShowDeleteModal(true);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  getFieldTypeName(item.ValueType),
                  <ValueOrNoDataLabel data={item.Title} />,
                  <ValueOrNoDataLabel data={item.Description} />,
                  <TrueFalseImage value={item.VisibleOnMobile} />,
                  <TrueFalseImage value={item.Mandatory} />,
                  // <TrueFalseImage value={item.AvailableOnReview} />,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(8)]
        }
      />
    </div>
  );
}
